import type { AxiosError } from 'axios';
import type { ChangeEvent, DragEvent, HTMLAttributes, InputHTMLAttributes, RefObject } from 'react';

import { useCallback, useEffect, useReducer, useRef } from 'react';

import type {
  DirectoryBaseConfig,
  DirectoryProgress,
  DirectoryStatus,
  FileProgress,
  FileUploadActions,
  FileUploadConfig,
  FileUploadState,
  FileUploadStatus,
  UploadFileOptions,
  UploadFileSystemDirectoryEntry,
  UploadFileSystemEntry,
  UploadFileSystemFileEntry,
} from '.';

import { DEFAULT_CONFIG, FILE_UPLOAD_INITIAL_STATE, fileUploadReducer } from '.';

export function useFileUpload<T extends DirectoryBaseConfig, R extends DirectoryBaseConfig>(config: FileUploadConfig<T, R>): [FileUploadState, FileUploadActions] {
  const _config = { ...DEFAULT_CONFIG, ...config };

  const { maxFiles, maxSize, directoryConfig, postponeUpload, flattenDirectoryUpload, onFilesChange, onFileUploaded, onFileUploadError, uploadFileFn, createDirectoryFn } = _config;

  const [state, dispatch] = useReducer(fileUploadReducer, FILE_UPLOAD_INITIAL_STATE);

  const dragCounterRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      dragCounterRef.current = 0;
      dispatch({ type: 'drag/reset' });
    };
  }, []);

  const generateUniqueId = useCallback((name?: string): string => {
    return `${name}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }, []);

  const createPreview = useCallback((file: File): string => {
    return URL.createObjectURL(file);
  }, []);

  const getDirectoryById = useCallback((dirs: DirectoryProgress[], id: string): DirectoryProgress | undefined => {
    for (const d of dirs) {
      if (d.id === id) return d;
      const found = getDirectoryById(d.subdirectories, id);
      if (found) return found;
    }
    return undefined;
  }, []);

  const getFileInDirectories = useCallback((dirs: DirectoryProgress[], fileId: string): { file: FileProgress; dir: DirectoryProgress } | undefined => {
    for (const d of dirs) {
      const file = d.files.find((f) => f.id === fileId);
      if (file) return { file, dir: d };
      const found = getFileInDirectories(d.subdirectories, fileId);
      if (found) return found;
    }
    return undefined;
  }, []);

  const parseDirectoryStructure = useCallback(
    (files: File[]): DirectoryProgress[] => {
      const dirMap = new Map<string, DirectoryProgress>();
      const rootDirectories: DirectoryProgress[] = [];

      files.forEach((file) => {
        if (!file.webkitRelativePath) return;

        const pathParts: string[] = file.webkitRelativePath.split('/');
        const fileName = pathParts[pathParts.length - 1];
        let currentPath: string = '';

        pathParts.slice(0, -1).forEach((dirName, index) => {
          const parentPath: string = currentPath;
          currentPath = currentPath ? `${currentPath}/${dirName}` : dirName;

          if (!dirMap.has(currentPath)) {
            const dirProgress: DirectoryProgress = {
              id: generateUniqueId(dirName),
              name: dirName,
              path: currentPath,
              status: 'creating',
              filesCount: 0,
              subdirectoriesCount: 0,
              uploadedFiles: 0,
              createdSubdirectories: 0,
              files: [],
              subdirectories: [],
            };

            dirMap.set(currentPath, dirProgress);

            if (parentPath && dirMap.has(parentPath)) {
              dirMap.get(parentPath)!.subdirectories.push(dirProgress);
              dirMap.get(parentPath)!.subdirectoriesCount++;
            } else if (index === 0) {
              rootDirectories.push(dirProgress);
            }
          }
        });

        if (currentPath && dirMap.has(currentPath)) {
          const fileProgress: FileProgress = {
            file,
            id: generateUniqueId(fileName),
            preview: createPreview(file),
            progress: 0,
            status: 'idle',
          };

          dirMap.get(currentPath)!.files.push(fileProgress);
          dirMap.get(currentPath)!.filesCount++;
        }
      });

      return rootDirectories;
    },
    [generateUniqueId, createPreview]
  );

  const createDirectory = useCallback(
    async (dir: DirectoryProgress, dirConfig?: T | R): Promise<R | undefined> => {
      if (!createDirectoryFn) {
        throw new Error('createDirectoryFn is not provided');
      }

      try {
        const response: R | undefined = await createDirectoryFn(dir.name, dirConfig);

        dispatch({
          type: 'directories/updateById',
          id: dir.id,
          patch: { status: 'created' },
        });

        return response;
      } catch (error) {
        dispatch({
          type: 'directories/updateById',
          id: dir.id,
          patch: { status: 'error', error: error as Error },
        });
        return;
      }
    },
    [createDirectoryFn]
  );

  const uploadDirectory = useCallback(
    async (dir: DirectoryProgress, dirConfig?: T | R): Promise<DirectoryStatus> => {
      const abortController = new AbortController();

      dispatch({
        type: 'directories/updateById',
        id: dir.id,
        patch: { status: 'uploading', abortController },
      });

      try {
        let createdDirResponse: R | undefined = dir.serverDirectory as R | undefined;

        if (!createdDirResponse) {
          createdDirResponse = await createDirectory(dir, dirConfig);
          if (!createdDirResponse) return 'error';

          dispatch({
            type: 'directories/updateById',
            id: dir.id,
            patch: { serverDirectory: createdDirResponse },
          });
        }

        const latestDirData = getDirectoryById(state.directories, dir.id);

        const latestFiles = latestDirData?.files ?? dir.files;

        const startedFileIds = new Set<string>();

        const fileUploads = latestFiles.map(async (fileObj): Promise<FileUploadStatus> => {
          if (fileObj.status === 'success' || fileObj.status === 'uploading') {
            return fileObj.status;
          }

          if (startedFileIds.has(fileObj.id)) {
            return fileObj.status;
          }
          startedFileIds.add(fileObj.id);

          dispatch({
            type: 'directories/updateFileById',
            directoryId: dir.id,
            fileId: fileObj.id,
            patch: { status: 'uploading', progress: 0, abortController },
          });

          const uploadOptions: UploadFileOptions<R> = {
            abortController,
            directory: createdDirResponse,
            progressCallback: (evt) => {
              if (evt.total) {
                dispatch({
                  type: 'directories/updateFileById',
                  directoryId: dir.id,
                  fileId: fileObj.id,
                  patch: {
                    progress: Math.round((evt.loaded / evt.total!) * 100),
                    status: 'uploading',
                  },
                });
              }
            },
          };
          try {
            await uploadFileFn(fileObj.file, uploadOptions);

            if (abortController.signal.aborted) {
              return 'aborted';
            }

            dispatch({
              type: 'directories/updateFileById',
              directoryId: dir.id,
              fileId: fileObj.id,
              patch: { status: 'success', progress: 100 },
            });
            dispatch({
              type: 'directories/incrementUploadedFiles',
              directoryId: dir.id,
            });

            onFileUploaded?.({
              ...fileObj,
              status: 'success',
              progress: 100,
            });
            return 'success';
          } catch (error) {
            const isAborted: boolean = (error as Error).name === 'CanceledError';
            if (!isAborted) {
              onFileUploadError?.(
                fileObj.file,
                error as Error,
                {
                  code: 'UPLOAD_FAILED',
                  message: 'File upload failed',
                },
                {
                  ...uploadOptions,
                  retryUpload: () => retryUpload(fileObj.id),
                }
              );
            }

            dispatch({
              type: 'directories/updateFileById',
              directoryId: dir.id,
              fileId: fileObj.id,
              patch: {
                status: isAborted ? 'aborted' : 'error',
                error: error as Error,
              },
            });
            return isAborted ? 'aborted' : 'error';
          }
        });

        const latestDirStateForSubdirs = getDirectoryById(state.directories, dir.id);

        const startedSubdirIds = new Set<string>();

        const subdirectoryUploads = (latestDirStateForSubdirs?.subdirectories ?? dir.subdirectories).map(async (subdir) => {
          if (subdir.status === 'success' || subdir.status === 'uploading') {
            return subdir.status;
          }

          if (startedSubdirIds.has(subdir.id)) {
            return subdir.status;
          }
          startedSubdirIds.add(subdir.id);

          const status = await uploadDirectory(subdir, createdDirResponse);
          if (status === 'success') {
            dispatch({
              type: 'directories/incrementCreatedSubdirectories',
              directoryId: dir.id,
            });
          }
          return status;
        });

        const fileResults = await Promise.all(fileUploads);
        const subdirResults = await Promise.all(subdirectoryUploads);

        const hasAborted = fileResults.includes('aborted') || subdirResults.includes('canceled');
        const hasError = fileResults.includes('error') || subdirResults.includes('error');

        let finalStatus: DirectoryProgress['status'] = 'success';
        if (hasAborted) {
          finalStatus = 'canceled';
        } else if (hasError) {
          finalStatus = 'error';
        }

        dispatch({
          type: 'directories/updateById',
          id: dir.id,
          patch: { status: finalStatus },
        });

        return finalStatus;
      } catch (error) {
        dispatch({
          type: 'directories/updateById',
          id: dir.id,
          patch: { status: 'error', error: error as Error },
        });
        return 'error';
      }
    },
    [createDirectory, uploadFileFn, onFileUploaded, onFileUploadError, getDirectoryById, state.directories]
  );

  const uploadFile = useCallback(
    async (fileObj: FileProgress) => {
      const abortController = new AbortController();
      const uploadOptions: UploadFileOptions<R> = {
        abortController,
        progressCallback: (evt) => {
          if (evt.total) {
            dispatch({
              type: 'files/updateById',
              id: fileObj.id,
              patch: {
                progress: evt.total ? Math.round((evt.loaded / evt.total) * 100) : fileObj.progress,
              },
            });
          }
        },
      };
      dispatch({
        type: 'files/updateById',
        id: fileObj.id,
        patch: { status: 'uploading', error: undefined, abortController },
      });
      try {
        await uploadFileFn(fileObj.file, uploadOptions);
        dispatch({
          type: 'files/updateById',
          id: fileObj.id,
          patch: { status: 'success', progress: 100 },
        });
        onFileUploaded?.({ ...fileObj, status: 'success', progress: 100 });
      } catch (err: unknown) {
        const error = err as AxiosError | Error;
        const isAborted = error.name === 'CanceledError';

        dispatch({
          type: 'files/updateById',
          id: fileObj.id,
          patch: {
            status: error.name === 'CanceledError' || error.message === 'canceled' ? 'aborted' : 'error',
            error: error,
          },
        });
        if (!isAborted) {
          onFileUploadError?.(
            fileObj.file,
            error,
            {
              code: 'UPLOAD_FAILED',
              message: 'File upload failed',
            },
            { ...uploadOptions, retryUpload: () => retryUpload(fileObj.id) }
          );
        }
      }
    },
    [onFileUploaded, onFileUploadError, uploadFileFn]
  );

  const uploadAll = useCallback(async () => {
    const filePromises = state.files.filter((fileObj) => fileObj.status === 'idle').map((fileObj) => uploadFile(fileObj));

    const dirPromises = state.directories.filter((dirObj) => dirObj.status === 'creating' || dirObj.status === 'idle').map((dirObj) => uploadDirectory(dirObj, directoryConfig));

    await Promise.all([...filePromises, ...dirPromises]);
  }, [state.files, state.directories, uploadFile, uploadDirectory, directoryConfig]);

  const addFiles = useCallback(
    (newFiles: FileList | File[], isDirectoryUpload: boolean = false) => {
      if (newFiles.length === 0) return;

      const rawFilesArray: File[] = Array.from(newFiles);

      if (isDirectoryUpload && !flattenDirectoryUpload) {
        const directories: DirectoryProgress[] = parseDirectoryStructure(rawFilesArray);

        dispatch({ type: 'directories/addMany', directories });

        directories.forEach((dir) => {
          uploadDirectory(dir, directoryConfig);
        });
        return;
      }
      const validFiles: FileProgress[] = [];

      for (const file of rawFilesArray) {
        if (file.size > maxSize) {
          const error = new Error('File is too large');
          onFileUploadError?.(file, error, {
            code: 'FILE_TOO_LARGE',
            message: 'File is too large',
          });
          continue;
        }

        validFiles.push({
          file,
          id: generateUniqueId(file.name),
          preview: createPreview(file),
          progress: 0,
          status: 'idle',
        });
      }

      if (validFiles.length === 0) return;

      dispatch({ type: 'files/addMany', files: validFiles });
    },
    [maxFiles, maxSize, flattenDirectoryUpload, generateUniqueId, createPreview, parseDirectoryStructure, uploadDirectory, directoryConfig, onFileUploadError]
  );

  const removeFile = useCallback((id: string) => {
    dispatch({ type: 'files/removeById', id });
  }, []);

  const removeDirectory = useCallback((id: string) => {
    dispatch({ type: 'directories/removeById', id });
  }, []);

  const abortFileUpload = useCallback(
    (id: string) => {
      const file = state.files.find((f) => f.id === id);
      file?.abortController?.abort();
      dispatch({ type: 'files/updateById', id, patch: { status: 'aborted' } });
    },
    [state.files]
  );

  const abortDirectoryUpload = useCallback(
    (id: string) => {
      const abortDirRecursive = (d: DirectoryProgress) => {
        d.abortController?.abort();

        d.files.forEach((f) => {
          if (f.abortController && (f.status === 'uploading' || f.status === 'idle')) {
            f.abortController.abort();
          }
          if (f.status === 'uploading' || f.status === 'idle') {
            dispatch({
              type: 'directories/updateFileById',
              directoryId: d.id,
              fileId: f.id,
              patch: { status: 'aborted' },
            });
          }
        });

        d.subdirectories.forEach(abortDirRecursive);

        dispatch({
          type: 'directories/updateById',
          id: d.id,
          patch: { status: 'canceled' },
        });
      };

      const root = getDirectoryById(state.directories, id);
      if (root) {
        abortDirRecursive(root);
      } else {
        dispatch({
          type: 'directories/updateById',
          id,
          patch: { status: 'canceled' },
        });
      }
    },
    [getDirectoryById, state.directories]
  );

  const clearFiles = useCallback(() => {
    state.files.forEach((f) => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });

    const revokePreviewsRecursive = (d: DirectoryProgress) => {
      d.files.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      d.subdirectories.forEach(revokePreviewsRecursive);
    };

    state.directories.forEach(revokePreviewsRecursive);

    dispatch({ type: 'files/clear' });
    dispatch({ type: 'directories/clear' });
  }, [state.directories, state.files]);

  const retryUpload = useCallback(
    (id: string) => {
      const fileObj = state.files.find((f) => f.id === id);
      if (fileObj) {
        uploadFile({
          ...fileObj,
          status: 'idle',
          progress: 0,
          error: undefined,
        });
        return;
      }

      const fileInDir = getFileInDirectories(state.directories, id);
      if (fileInDir) {
        dispatch({
          type: 'directories/updateFileById',
          directoryId: fileInDir.dir.id,
          fileId: id,
          patch: {
            status: 'idle',
            progress: 0,
            error: undefined,
          },
        });

        if (fileInDir.dir.serverDirectory) {
          const abortController = new AbortController();
          const uploadOptions: UploadFileOptions<R> = {
            abortController,
            directory: fileInDir.dir.serverDirectory as R,
            progressCallback: (evt) => {
              if (evt.total) {
                dispatch({
                  type: 'directories/updateFileById',
                  directoryId: fileInDir.dir.id,
                  fileId: id,
                  patch: {
                    progress: Math.round((evt.loaded / evt.total!) * 100),
                    status: 'uploading',
                  },
                });
              }
            },
          };

          uploadFileFn(fileInDir.file.file, uploadOptions)
            .then(() => {
              dispatch({
                type: 'directories/updateFileById',
                directoryId: fileInDir.dir.id,
                fileId: id,
                patch: { status: 'success', progress: 100 },
              });
              dispatch({
                type: 'directories/incrementUploadedFiles',
                directoryId: fileInDir.dir.id,
              });

              onFileUploaded?.({
                ...fileInDir.file,
                status: 'success',
                progress: 100,
              });
            })
            .catch((error) => {
              const isAborted = (error as Error).name === 'CanceledError';
              dispatch({
                type: 'directories/updateFileById',
                directoryId: fileInDir.dir.id,
                fileId: id,
                patch: {
                  status: isAborted ? 'aborted' : 'error',
                  error: error as Error,
                },
              });
            });
        }
        return;
      }

      const dirObj = getDirectoryById(state.directories, id);
      if (dirObj) {
        uploadDirectory(dirObj, directoryConfig);
      }
    },
    [directoryConfig, dispatch, getDirectoryById, getFileInDirectories, onFileUploaded, state.directories, state.files, uploadDirectory, uploadFile, uploadFileFn]
  );

  const abortAllUploads = useCallback(() => {
    state.files.forEach((file) => {
      if (file.abortController && (file.status === 'uploading' || file.status === 'idle')) {
        file.abortController.abort();
      }
      if (file.status === 'uploading' || file.status === 'idle') {
        dispatch({
          type: 'files/updateById',
          id: file.id,
          patch: { status: 'aborted' },
        });
      }
    });

    const abortDirectoryRecursive = (dir: DirectoryProgress) => {
      if (dir.abortController && (dir.status === 'uploading' || dir.status === 'creating')) {
        dir.abortController.abort();
      }

      dir.files.forEach((file) => {
        if (dir.status === 'uploading' || dir.status === 'creating') {
          dispatch({
            type: 'directories/updateFileById',
            directoryId: dir.id,
            fileId: file.id,
            patch: { status: 'aborted' },
          });
        }
      });

      dir.subdirectories.forEach(abortDirectoryRecursive);

      if (dir.status === 'uploading' || dir.status === 'creating') {
        dispatch({
          type: 'directories/updateById',
          id: dir.id,
          patch: { status: 'canceled' },
        });
      }
    };

    state.directories.forEach(abortDirectoryRecursive);
  }, [dispatch, state.directories, state.files]);

  const openFileDialog = useCallback((ref: RefObject<HTMLInputElement | null>) => {
    if (ref.current) ref.current.click();
  }, []);

  const createInputProps = useCallback(
    <T extends InputHTMLAttributes<HTMLInputElement>>(props: T = {} as T) => {
      const localRef = useRef<HTMLInputElement>(null);

      return {
        ref: localRef as RefObject<HTMLInputElement>,
        openFileDialog: () => openFileDialog(localRef),
        props: {
          ...props,
          type: 'file',
          onChange: (e: ChangeEvent<HTMLInputElement>): void => {
            props.onChange?.(e);
            if (e.target.files && e.target.files.length > 0) {
              const files: File[] = Array.from(e.target.files);
              const isDirectoryUpload: boolean = files.some((f) => f.webkitRelativePath);
              addFiles(files, isDirectoryUpload);
            }
            e.target.value = '';
          },
        },
      };
    },
    [addFiles, openFileDialog]
  );

  const createDropzoneProps = useCallback(
    <T extends HTMLAttributes<HTMLElement>>(props: T = {} as T) => {
      return {
        props: {
          ...props,
          onDragEnter: (e: DragEvent<HTMLElement>): void => {
            e.preventDefault();
            e.stopPropagation();
            dragCounterRef.current += 1;
            if (dragCounterRef.current > 0) {
              dispatch({ type: 'drag/set', isDragging: true });
            }
            props.onDragEnter?.(e);
          },
          onDragLeave: (e: DragEvent<HTMLElement>): void => {
            e.preventDefault();
            e.stopPropagation();
            dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
            if (dragCounterRef.current === 0) {
              dispatch({ type: 'drag/set', isDragging: false });
            }
            props.onDragLeave?.(e);
          },
          onDragOver: (e: DragEvent<HTMLElement>): void => {
            e.preventDefault();
            e.stopPropagation();
            props.onDragOver?.(e);
          },
          onDrop: async (e: DragEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounterRef.current = 0;
            dispatch({ type: 'drag/set', isDragging: false });

            const dt = e.dataTransfer;
            const items = dt.items;

            if (items && items.length > 0) {
              const entries = Array.from(items)
                .map((item) => {
                  const itemWithEntry = item as unknown as {
                    webkitGetAsEntry?: () => UploadFileSystemEntry | null;
                  };
                  if (typeof itemWithEntry.webkitGetAsEntry === 'function') {
                    return itemWithEntry.webkitGetAsEntry();
                  }
                  return null;
                })
                .filter((entry): entry is UploadFileSystemEntry => entry !== null);

              if (entries.length > 0) {
                const traverseEntry = async (entry: UploadFileSystemEntry, path: string = ''): Promise<File[]> => {
                  if (entry.isFile) {
                    const fileEntry = entry as UploadFileSystemFileEntry;
                    return new Promise<File[]>((resolve) => {
                      fileEntry.file((file: File) => {
                        Object.defineProperty(file, 'webkitRelativePath', {
                          value: path + file.name,
                        });
                        resolve([file]);
                      });
                    });
                  } else if (entry.isDirectory) {
                    const dirEntry = entry as UploadFileSystemDirectoryEntry;
                    const dirReader = dirEntry.createReader();
                    const entries: UploadFileSystemEntry[] = [];

                    const readBatch = (): Promise<UploadFileSystemEntry[]> => {
                      return new Promise((resolve) => {
                        dirReader.readEntries(
                          (batch: UploadFileSystemEntry[]) => resolve(batch),
                          () => resolve([])
                        );
                      });
                    };

                    try {
                      let batch = await readBatch();
                      while (batch.length > 0) {
                        entries.push(...batch);
                        batch = await readBatch();
                      }
                    } catch {
                      // Ignore errors reading directory
                    }

                    const files: File[] = [];
                    const promises = entries.map((subEntry) => traverseEntry(subEntry, path + entry.name + '/'));
                    const results = await Promise.all(promises);
                    results.forEach((res) => files.push(...res));
                    return files;
                  }
                  return [];
                };

                const files: File[] = [];
                const promises = entries.map((entry) => traverseEntry(entry));
                const results = await Promise.all(promises);
                results.forEach((res) => files.push(...res));

                if (files.length > 0) {
                  const isDirectoryUpload = files.some((f) => f.webkitRelativePath.split('/').length > 1);
                  addFiles(files, isDirectoryUpload);
                  props.onDrop?.(e);
                  return;
                }
              }
            }

            if (dt.files.length) {
              const files: File[] = Array.from(dt.files);
              const hasWebkitRelativePath = files.some((file) => file.webkitRelativePath);
              addFiles(dt.files, hasWebkitRelativePath);
            }

            props.onDrop?.(e);
          },
        },
      };
    },
    [addFiles]
  );

  useEffect(() => {
    onFilesChange?.(state.files);
  }, [state.files, onFilesChange]);

  useEffect(() => {
    if (!postponeUpload) {
      const hasIdleFiles = state.files.some((f) => f.status === 'idle');
      if (hasIdleFiles) {
        uploadAll();
      }
    }
  }, [postponeUpload, state.files.length, uploadAll]);

  return [
    state,
    {
      addFiles,
      removeFile,
      removeDirectory,
      clearFiles,
      retryUpload,
      retryDirectoryUpload: retryUpload,
      abortFileUpload,
      abortDirectoryUpload,
      abortAllUploads,
      createInputProps,
      createDropzoneProps,
      ...(postponeUpload ? { uploadAll } : {}),
    },
  ];
}
